import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  type Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-choose-instance',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    SpinnerComponent,
    ButtonComponent,
    IconComponent,
    TranslatePipe,
  ],
  templateUrl: './choose-instance.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './choose-instance.component.css',
})
export class ChooseInstanceComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private assetInstanceService = inject(AssetInstanceService);
  private breadcrumbService = inject(BreadcrumbService);
  private logger = inject(LoggerService).getLogger('ChooseInstanceComponent');

  checkPoint!: Signal<CheckPoint | null>;
  checkPointLoading!: Signal<boolean>;
  checkPointError!: Signal<string | null>;

  instances!: Signal<AssetInstance[]>;
  instancesLoading!: Signal<boolean>;
  instancesError!: Signal<string | null>;

  form = signal<Form | null>(null);
  formLoading = signal(true);

  loading = computed(
    () => this.checkPointLoading?.() || this.instancesLoading?.() || this.formLoading(),
  );
  error = computed(() => this.checkPointError?.() || this.instancesError?.() || null);

  initError = signal<string | null>(null);

  checkpointId!: string;
  formId!: string;

  constructor() {
    effect(() => {
      const checkPoint = this.checkPoint?.();
      if (checkPoint) {
        this.breadcrumbService.setDynamicLabel(checkPoint.name);
      }

      // Auto-redirect if no instances once loading is done
      const instances = this.instances?.();
      if (!this.loading() && instances !== undefined && instances.length === 0) {
        this.logger.debug('No instances found, auto-redirecting to submission form');
        void this.router.navigate(['/submission', this.checkpointId, this.formId]);
      }
    });
  }

  async ngOnInit() {
    this.checkpointId = this.route.snapshot.paramMap.get('checkpointId') ?? '';
    this.formId = this.route.snapshot.paramMap.get('formId') ?? '';

    if (!this.checkpointId || !this.formId) {
      this.initError.set('Missing checkpoint or form ID');
      return;
    }

    const checkPointQuery = this.checkPointService.createQueryById(this.checkpointId);
    this.checkPoint = checkPointQuery.data;
    this.checkPointLoading = checkPointQuery.loading;
    this.checkPointError = checkPointQuery.error;

    const instancesQuery = this.assetInstanceService.createQueryByCheckpointId(this.checkpointId);
    this.instances = instancesQuery.data;
    this.instancesLoading = instancesQuery.loading;
    this.instancesError = instancesQuery.error;

    try {
      const form = await this.formService.getById(this.formId);
      this.form.set(form);
    } finally {
      this.formLoading.set(false);
    }
  }

  selectInstance(instance: AssetInstance) {
    void this.router.navigate(['/submission', this.checkpointId, this.formId, instance.id]);
  }

  skipInstance() {
    void this.router.navigate(['/submission', this.checkpointId, this.formId]);
  }

  goBack() {
    void this.router.navigate(['/submission', this.checkpointId]);
  }
}
